<template>
    <modal :name="name"
           height="95%"
           width="95%"
           scrollable
           :clickToClose="false"
           @before-open="beforeOpen"
           @before-close="beforeClose">

        <div class="h-100 overflow-auto">

            <div v-if="isLoading" class="row mx-0 h-100">
                <div class="col align-self-center text-center">
                    <h5 class="text-muted">
                        Loading...
                    </h5>
                </div>
            </div>

            <template v-else-if="!isTemplateSelection">
                <div class="row m-0 text-left border-bottom rounded-top bg-white p-3">
                    <div class="col-6 align-self-center pl-0 text-left">
                        <AppEditableText
                            :value="subtype.display"
                            @update="onTitleUpdate"
                            class="font-18 d-inline-block text-semi-bold">
                            <div slot="popover-header" class="text-muted text-left">
                                Subtype name
                            </div>
                            <template slot="popover-footer" slot-scope="{value}">
                                <div v-if="!value" class="text-danger text-left font-12">
                                    Value is required
                                </div>
                            </template>
                        </AppEditableText>
                    </div>
                    <div class="col-6 align-self-center text-right">
                        <div class="custom-control custom-switch d-inline-block mr-3">
                            <input class="custom-control-input" type="checkbox"
                                   :value="subtype.isTemplate"
                                   :checked="subtype.isTemplate"
                                   @change="onAddToTemplatedToggle"
                                   id="isTemplate">
                            <label
                                v-tooltip="'This form will appear in templates library for quick configuration of similar forms'"
                                class="custom-control-label text-dark pt-1"
                                for="isTemplate">
                                Use as template
                            </label>
                        </div>
                        <button v-loading.btn="isChangesSaving"
                                @click="saveChanges"
                                class="btn btn-sm btn-outline-primary"
                                style="width: 120px">
                            Save changes
                        </button>
                        <button @click="closeModal"
                                class="btn btn-sm btn-outline-info ml-3"
                                style="width: 80px">
                            Cancel
                        </button>
                    </div>
                </div>

                <splitpanes class="default-theme w-100" style="height: calc(100% - 64px)">
                    <pane size="20" min-size="20" max-size="75" style="min-width: 240px">
                        <FormCatalog
                            :sections="sections"
                            :fields="fields"
                            :selection="selection"
                            :updateFields="updateFields"
                            :updateSections="updateSections"
                            :changeSelection="changeSelection">
                            <template slot="catalog-footer">
                                <div @click="openToolbarSettings"
                                     class="catalog-extensions media border-top">
                                    <div class="catalog-footer-icon">
                                        <i class="fas fa-screwdriver-wrench"></i>
                                    </div>
                                    <div class="media-body">
                                        Toolbar settings
                                    </div>
                                </div>
                                <!-- v-tooltip="{content: 'Extend the form with additional plugins', delay: 1000}" -->
                                <div @click="$modal.show('form-extensions')"
                                     class="catalog-extensions media border-top">
                                    <div class="catalog-footer-icon">
                                        <i class="fa fa-puzzle-piece"></i>
                                    </div>
                                    <div class="media-body">
                                        Extensions
                                        <span v-if="extensions.length !== 0">
                                            [{{ extensions.length }}]
                                        </span>
                                        <DocumentViewerExtensionHintsToggle
                                            @click.native.stop
                                            label="Hints" class="hints-toggle"/>
                                    </div>
                                </div>
                                <div @click="onOpenTemplatesLibrary"
                                     v-tooltip="{content: 'Select preinstalled form template', delay: 1000}"
                                     class="catalog-templates media text-muted border-top">
                                    <div class="catalog-footer-icon">
                                        <i class="fa fa-books"></i>
                                    </div>
                                    <div class="media-body">
                                        Templates library
                                    </div>
                                </div>
                                <div @click="$modal.show('quick-actions')"
                                     class="catalog-extensions media border-top">
                                    <div class="catalog-footer-icon">
                                        <i class="fas fa-bolt"></i>
                                    </div>
                                    <div class="media-body">
                                        Quick actions
                                        <span v-if="quickActions.length !== 0">
                                            [{{ quickActions.length }}]
                                        </span>
                                    </div>
                                </div>
                            </template>
                        </FormCatalog>
                    </pane>
                    <pane size="60" min-size="40" max-size="75" class="border-left pt-2">
                        <div v-if="sections.length === 0 && fields.length === 0"
                             class="text-center text-muted p-3">
                            <div v-tooltip="'Use +Add button on the left sidebar'">
                                Start adding a new elements
                            </div>
                            <div class="mt-2">
                                or select a preset from
                                <span @click="onOpenTemplatesLibrary"
                                      v-tooltip="{content: 'Select preinstalled form template', delay: 1000}"
                                      class="catalog-templates text-muted py-0 px-0">
                                    <i class="fa fa-books"></i>
                                    Templates library
                                </span>
                            </div>
                        </div>
                        <DocumentViewerForm
                            v-else
                            :selection="selection"
                            :changeSelection="changeSelection"
                            class="p-2"/>
                    </pane>
                    <pane size="20" min-size="20" max-size="75" class="border-left" style="min-width: 328px">
                        <div v-if="!selection.section && !selection.field" class="text-muted p-3">
                            No item selected
                        </div>
                        <FieldEditor
                            v-else-if="selectedField"
                            :key="selectedField.path"
                            :field="selectedField"
                            :updateField="f => updateFields([f])"
                            @delete="deleteField(selectedField)"/>
                        <SectionEditor
                            v-else-if="selectedSection"
                            :section="selectedSection"
                            @delete="deleteSection(selectedSection)"
                            :updateSection="s => updateSections([s])">
                            <div slot="header">
                                <button
                                    v-if="selectedSection.key === FormSystemSections.LANDED_COST_LINES && sections.some(s => s.key === FormSystemSections.LINES)"
                                    @click="onSyncLandedCostLineWithLineSettings"
                                    class="btn btn-sm btn-block btn-outline-primary mb-3">
                                    Sync with lines section
                                </button>
                            </div>
                            <div v-if="selectedSection.key === FormSystemSections.RELATED_DOCS"
                                 slot="editor-footer">
                                <RelatedDocsSectionSettings
                                    @update="params => onUpdateSectionParams(selectedSection.key, params)"
                                    :params="selectedSection.params"/>
                            </div>
                        </SectionEditor>
                    </pane>
                </splitpanes>
            </template>
        </div>

        <FormToolbarSettingsEditor
            modalName="form-toolbar-settings"
            :value="toolbarSettings"
            @update="updateToolbarSettings"/>
        <FormExtensions
            name="form-extensions"
            :settings="settings"
            :extensions="extensions"
            @update="updateExtensions"/>
        <FormTemplates
            name="form-templates"
            :section="subtype.section"
            @select="onTemplateSelect"
            @cancel="onTemplateSelectCancel"/>
        <FormQuickActions
            name="quick-actions"
            :actions="quickActions"
            :settings="settings"
            @update="updateQuickActions"/>
    </modal>
</template>

<script>
import axios from 'axios'
import {Pane, Splitpanes} from 'splitpanes'
import 'splitpanes/dist/splitpanes.css'
import {SUCCESS} from '../../../../store/modules/ui/UI'
import DocumentViewerForm from '../../single/view/DocumentViewerForm'
import FormCatalog from './catalog/FormCatalog'
import FieldEditor from './editor/field/FieldEditor'
import SectionEditor from './editor/section/SectionEditor'
import FormExtensions from './extensions/FormExtensions'
import FormQuickActions from './quick-actions/FormQuickActions'
import FormTemplates from './templates/FormTemplates'
import AppEditableText from '../../../../components/shared/AppEditableText'
import {mapActions, mapGetters, mapMutations} from 'vuex'
import {
    DOC_SETTINGS_FETCH,
    DOC_SETTINGS_REFRESH_VISIBLE_FIELDS_WITH_STATE_RULES_APPLIED,
    DOC_SETTINGS_SET_IS_EDIT_MODE,
    DOC_SETTINGS_UPDATE
} from '@/store/modules/doc-viewer/settings'
import DocumentViewerExtensionHintsToggle from '../../single/view/extensions/DocumentViewerExtensionHintsToggle'
import {withConfirmation} from '@/decorators/withConfirmation'
import {FormSystemSections} from '@/types/document-viewer/section'
import {getFieldPathMeta} from '@/types/document-viewer/field/modules/path'
import {DOC_SET} from '@/store/modules/doc-viewer'
import FormToolbarSettingsEditor from '@/views/documents/subtypes/settings/toolbar/FormToolbarSettingsEditor.vue'
import RelatedDocsSectionSettings
    from '@/views/documents/subtypes/settings/editor/section/related-docs/RelatedDocsSectionSettings.vue'

export default {
    name: 'DocSubtypeSettingsModal',
    components: {
        RelatedDocsSectionSettings,
        FormToolbarSettingsEditor,
        DocumentViewerExtensionHintsToggle,
        AppEditableText,
        FormTemplates,
        FormExtensions,
        FormQuickActions,
        FieldEditor,
        SectionEditor,
        FormCatalog,
        DocumentViewerForm,
        Splitpanes,
        Pane
    },
    props: {
        name: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            subtype: {
                property: '',
                section: '',
                display: '',
                isTemplate: false,
                global: false
            },

            selection: {
                field: null,
                section: null
            },

            isLoading: false,
            isTemplateSelection: false,
            isChangesSaving: false,
            savedSettings: {},

            FormSystemSections
        }
    },
    computed: {
        ...mapGetters(['isStaff']),
        ...mapGetters('doc_viewer', [
            'doc',
            'docWithNormalizedParts'
        ]),

        ...mapGetters('doc_viewer_settings', [
            'settings',
            'toolbarSettings',
            'sections',
            'fields',
            'extensions',
            'quickActions'
        ]),

        selectedSection() {
            if (!this.selection.field) {
                return this.sections.find(s => s.key === this.selection.section)
            }
            return null
        },

        selectedField() {
            return this.fields.find(f => f.section.key === this.selection.section && f.path === this.selection.field)
        }
    },
    methods: {
        ...mapMutations('doc_viewer_settings', {
            updateSettings: DOC_SETTINGS_UPDATE,
            setIsSettingsEditMode: DOC_SETTINGS_SET_IS_EDIT_MODE
        }),

        ...mapActions('doc_viewer_settings', {
            fetchSettings: DOC_SETTINGS_FETCH,
            refreshVisibleFieldsWithRulesApplied: DOC_SETTINGS_REFRESH_VISIBLE_FIELDS_WITH_STATE_RULES_APPLIED
        }),

        ...mapActions('doc_viewer', {
            setDocState: DOC_SET
        }),

        closeModal() {
            this.$modal.hide(this.name)
        },

        async beforeOpen({params}) {
            this.isLoading = true
            this.setIsSettingsEditMode(true)
            this.prevDoc = _.cloneDeep(this.docWithNormalizedParts)

            let mockDoc
            if (this.doc.uuid) { // settings edit modal opened from doc-viewer modal
                mockDoc = {
                    ...this.prevDoc,
                    // to speedup settings edit we setup only first line
                    lines: _.isArray(this.prevDoc.lines) ? [this.prevDoc.lines[0]] : []
                }
            } else {
                mockDoc = {
                    uuid: 'test',
                    users: [],
                    lines: [],
                    notes: [],
                    custom: {},
                    company: {
                        custom: {}
                    }
                }
            }
            this.setDocState(mockDoc)
            this.subtype = await this.fetchSettings({type: params.docTypeKey, subtype: params.docSubtypeKey})
            this.$watch(vm => vm.settings, this.onSettingsChange, {deep: true})
            this.isLoading = false
        },

        beforeClose() {
            this.setDocState(this.prevDoc)
            this.setIsSettingsEditMode(false)
            this.$bus.$emit('DOC_SETTINGS_RELOAD')
            Object.assign(this.$data, this.$options.data.apply(this))
        },

        onSettingsChange() {
            this.refreshVisibleFieldsWithRulesApplied()
        },

        updateSections(updatedSections = []) {
            const allSections = _.cloneDeep(this.sections)
            let idx
            updatedSections.forEach(section => {
                idx = allSections.findIndex(s => s.key === section.key)
                if (idx !== -1) {
                    allSections[idx] = section
                } else { // add
                    allSections.push(section)
                }
            })
            this.updateSettings({prop: 'sections', data: allSections.sort((a, b) => a.order - b.order)})
        },

        updateFields(updatedFields = []) {
            const allFields = _.cloneDeep(this.fields)
            let idx
            updatedFields.forEach(field => {
                idx = this.fields.findIndex(f => f.path === field.path)
                if (idx !== -1) {
                    allFields[idx] = field
                } else { // add
                    allFields.push(field)
                }
            })
            this.updateSettings({prop: 'fields', data: allFields.sort((a, b) => a.section.order - b.section.order)})
        },

        openToolbarSettings() {
            this.$modal.show('form-toolbar-settings', {
                toolbarSettings: this.toolbarSettings,
                onSave: this.updateToolbarSettings
            })
        },

        updateToolbarSettings(toolbarSettings) {
            this.updateSettings({prop: 'toolbar', data: toolbarSettings})
        },

        updateExtensions(extensions) {
            this.updateSettings({prop: 'extensions', data: extensions})
        },

        updateQuickActions(quickActions) {
            this.updateSettings({prop: 'quickActions', data: quickActions})
            this.$modal.hide('quick-actions')
        },

        changeSelection(s) {
            if (this.selection.field && this.selection.field === s.field && this.selection.section === s.section) {
                return
            }
            this.selection = s
        },

        deleteSection(section) {
            if (section && section.key) {
                if (this.selectedSection && this.selectedSection.name === section.name) {
                    this.selection.section = null
                }
                const sections = _.cloneDeep(this.sections)
                const idx = sections.findIndex(s => s.key === section.key)
                sections.splice(idx, 1)
                this.updateSettings({prop: 'sections', data: sections})

                this.fields
                    .filter(f => f.section.key === section.key)
                    .forEach(f => {
                        this.deleteField(f)
                    })
            }
        },

        deleteField(field) {
            if (field && field.path) {
                if (this.selectedField && this.selectedField.path === field.path) {
                    this.selection.field = null
                    this.selection.section = null
                }
                const fields = _.cloneDeep(this.fields)
                const idx = fields.findIndex(f => f.path === field.path)
                fields.splice(idx, 1)
                this.updateSettings({prop: 'fields', data: fields})
            }
        },

        saveChanges() {
            this.isChangesSaving = true
            const data = {
                ...this.subtype,
                settings: this.settings
            }
            axios.post(`documents/types/${this.subtype.section}/subtypes/createOrUpdate`, data)
                .then(() => {
                    this.$bus.$emit('DOC_SUBTYPE_UPDATE', data)
                    this.$snackbar.show({type: SUCCESS, text: 'Changes saved'})
                })
                .catch(error => {
                    this.$snackbar.show({error})
                })
                .finally(() => {
                    this.isChangesSaving = false
                })
        },

        onOpenTemplatesLibrary() {
            this.isTemplateSelection = true
            this.savedSettings = _.cloneDeep(this.settings)
            this.$modal.show('form-templates')
        },

        async onTemplateSelect(settings) {
            this.updateSettings({prop: 'sections', data: _.cloneDeep(settings.sections)})
            this.updateSettings({prop: 'fields', data: _.cloneDeep(settings.fields)})
            this.updateSettings({prop: 'extensions', data: _.cloneDeep(settings.extensions)})
            await this.$nextTick()
            this.isTemplateSelection = false
        },

        async onTemplateSelectCancel() {
            this.updateSettings({prop: 'sections', data: _.cloneDeep(this.savedSettings.sections)})
            this.updateSettings({prop: 'fields', data: _.cloneDeep(this.savedSettings.fields)})
            this.updateSettings({prop: 'extensions', data: _.cloneDeep(this.savedSettings.extensions)})
            await this.$nextTick()
            this.isTemplateSelection = false
        },

        onTitleUpdate(display) {
            this.$set(this.subtype, 'display', display)
        },

        onSyncLandedCostLineWithLineSettings() {
            withConfirmation({
                onConfirm: this.syncLandedCostLineWithLineSettings,
                title: 'Synchronize landed-cost line settings',
                description: 'This will cause the landed-cost line settings to be completely overwritten with line settings for each field.<br> <br> Do you want to continue?'
            })
        },

        onUpdateSectionParams(sectionKey, params) {
            const sections = _.cloneDeep(this.sections)
            const idx = sections.findIndex(s => s.key === sectionKey)
            if (idx !== -1) {
                sections[idx].params = params
                this.updateSections(sections)
            }
        },

        syncLandedCostLineWithLineSettings() {
            const fields = _.cloneDeep(this.fields.filter(f => f.section && f.section.key !== FormSystemSections.LANDED_COST_LINES))
            const lineFields = _.cloneDeep(this.fields.filter(f => f.section && f.section.key === FormSystemSections.LINES))
            lineFields.forEach(f => {
                f.path = `lcLines.${getFieldPathMeta(f.path).prop}`
                f.section.key = FormSystemSections.LANDED_COST_LINES
                fields.push(f)
            })

            this.updateSettings({prop: 'fields', data: _.cloneDeep(fields)})
        },

        onAddToTemplatedToggle() {
            if (!this.subtype.isTemplate && this.isStaff) {
                withConfirmation({
                    onConfirm: () => {
                        this.subtype.global = true
                    },
                    onCancel: () => {
                        this.subtype.global = false
                    },
                    title: 'Make the template global?',
                    description: 'The global template will appear in the templates library for all teams'
                })
            }
            this.subtype.isTemplate = !this.subtype.isTemplate
            if (!this.subtype.isTemplate) {
                this.subtype.global = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.catalog-extensions,
.catalog-templates {
  position: relative;
  height: 36px;
  font-weight: 500;
  background: white;
  padding: 8px 16px;

  &:hover {
    cursor: pointer;
  }
}

.catalog-extensions {
  color: $color-purple-light;
}

.hints-toggle {
  position: absolute;
  top: 1px;
  right: 12px;
}

.catalog-footer-icon {
  width: 20px;
}
</style>

<style lang="scss">
.splitpanes.default-theme .splitpanes__pane {
  background-color: white;
}
</style>
